import React, { useState, useEffect } from "react";
import styled, { ThemeProvider } from "styled-components";
import defaultTheme from "theme";
import PrimaryButton from "components/button/PrimaryButton";
import SecondaryButton from "components/button/SecondaryButton";
import AppLogo from "components/labels/AppLogo";
import Loader from "components/loader/Loader";
import i18n from "i18next";
import { useSelector, useDispatch } from "react-redux";
import {
  userNameSelector,
  userEmailSelector,
  Routes,
  getOAuth2Code,
  setSignInRedirectPathAction,
  isAppInWhiteLabelMode,
  accountSubscriptionStatusSelector,
  SUBSCRIPTION_STATUS,
  isAccountSuspendedSelector
} from "@kubera/common";
import { useNavigate, useSearchParams } from "react-router-dom";

const Container = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: calc(100% - 50px);
  min-height: 100vh;
  background: #ffffff;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 423px;
  min-width: 423px;
  height: fit-content;
`;

const SignInForm = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: ${props => (props.isLoading ? "center" : "flex-start")};
  align-items: ${props => (props.isLoading ? "center" : "flex-start")};
  min-height: 530px;
  margin-top: 8px;
  padding: 50px;
  border: 1px solid #000000;
  box-sizing: border-box;
  background: ${props => props.theme.dialogContentContainerBG};
`;

const Title = styled.div`
  font-style: normal;
  font-weight: bold;
  font-size: 30px;
  line-height: 36px;
  letter-spacing: -0.015em;
  font-feature-settings: "pnum" on, "lnum" on, "ss01" on;
  white-space: pre-wrap;
`;

const UserDesc = styled.div`
  font-size: 12px;
  line-height: 120%;
  opacity: 0.5;
  margin-top: 7px;
`;

const Desc = styled.div`
  font-size: 14px;
  line-height: 150%;
  margin-top: 40px;
  a {
    color: ${props => props.theme.linkColor};
  }
`;

const ButtonContainer = styled.div`
  margin-top: 95px;
  display: flex;
  justify-content: flex-start;
`;

const MyPrimaryButton = styled(PrimaryButton)`
  min-width: 152px;
`;

const DenyButton = styled(SecondaryButton)`
  min-width: 98px;
  margin-left: 10px;
`;

const LoadingText = styled.p`
  font-size: 12px;
  letter-spacing: -1.5%;
`;

const MyLoader = styled(Loader)`
  height: fit-content;
`;

const OAuthSignInComponent = ({ isSignedIn }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [searchParams] = useSearchParams();
  const name = useSelector(userNameSelector);
  const email = useSelector(userEmailSelector);
  const subscription = useSelector(accountSubscriptionStatusSelector);
  const isSuspended = useSelector(isAccountSuspendedSelector);
  const dispatch = useDispatch();

  useEffect(() => {
    if (isAppInWhiteLabelMode() || isSuspended) {
      handleDeny();
    }

    if (!isSignedIn) {
      dispatch(
        setSignInRedirectPathAction({
          pathname: Routes.OAUTH_SIGNIN,
          search: window.location.search
        })
      );
      navigate(Routes.NET_WORTH);
    }
  }, [isAppInWhiteLabelMode, isSignedIn, subscription, dispatch, navigate]); // handle redirects

  const handleAllow = () => {
    const clientId = searchParams.get("client_id");
    const redirectUri = searchParams.get("redirect_uri");
    const responseType = searchParams.get("response_type");
    const state = searchParams.get("state");

    if (!clientId) {
      console.log("empty client id, make sure it is set correctly in gpt");
    }

    if (!clientId || !redirectUri || !responseType) {
      setIsError(true);
      return;
    }

    if (responseType !== "code") {
      setIsError(true);
      return;
    }

    setIsLoading(true);

    getOAuth2Code({
      clientId,
      redirectUri,
      responseType,
      state
    })
      .then(response => {
        window.location.href = response.url;
      })
      .catch(error => {
        setIsError(true);
        setIsLoading(false);
      });
  };

  const handleDeny = () => {
    const redirectUri = searchParams.get("redirect_uri");
    const state = searchParams.get("state");

    if (redirectUri) {
      try {
        const redirectUrl = new URL(redirectUri);
        redirectUrl.searchParams.append("error", "access_denied");
        if (state) {
          redirectUrl.searchParams.append("state", state);
        }
        window.location.href = redirectUrl.toString();
      } catch (error) {
        navigate(Routes.NET_WORTH);
      }
    } else {
      navigate(Routes.NET_WORTH);
    }
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      <Container>
        <ContentContainer>
          <AppLogo />
          <SignInForm isLoading={isLoading}>
            {isLoading ? (
              <>
                <MyLoader size={30} />
                <LoadingText>{i18n.t("oauth.loading")}</LoadingText>
              </>
            ) : (
              <>
                <Title>{i18n.t("oauth.title")}</Title>
                <UserDesc>
                  {name}
                  <br />
                  {email}
                </UserDesc>
                {isError ? (
                  <>
                    <Desc
                      dangerouslySetInnerHTML={{
                        __html: i18n.t("oauth.error")
                      }}
                    ></Desc>
                    <ButtonContainer>
                      <MyPrimaryButton
                        title={i18n.t("retry")}
                        onClick={() => {
                          setIsError(false);
                          handleAllow();
                        }}
                      />
                    </ButtonContainer>
                  </>
                ) : (
                  <>
                    <Desc
                      dangerouslySetInnerHTML={{
                        __html: i18n.t("oauth.desc")
                      }}
                    ></Desc>
                    <ButtonContainer>
                      <MyPrimaryButton title={i18n.t("oauth.allow")} onClick={handleAllow} />
                      <DenyButton title={i18n.t("oauth.deny")} onClick={handleDeny} />
                    </ButtonContainer>
                  </>
                )}
              </>
            )}
          </SignInForm>
        </ContentContainer>
      </Container>
    </ThemeProvider>
  );
};

export default OAuthSignInComponent;
